import { createClient, SanityClient } from '@sanity/client'
import imageUrlBuilder from '@sanity/image-url'
import type { SanityImageSource } from '@sanity/image-url/lib/types/types'
import type { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder'

// Extend the type to include string option
type ExtendedSanityImageSource = SanityImageSource | string;

const projectId = import.meta.env.VITE_SANITY_PROJECT_ID

// Only create client if projectId is configured
export const client: SanityClient = projectId
  ? createClient({
      projectId,
      dataset: import.meta.env.VITE_SANITY_DATASET || 'production',
      useCdn: true,
      apiVersion: '2024-01-01',
    })
  : (null as unknown as SanityClient)

const builder = projectId ? imageUrlBuilder(client) : null

// Stub builder for when Sanity is not configured
const stubBuilder: ImageUrlBuilder = {
  width: () => stubBuilder,
  height: () => stubBuilder,
  url: () => '',
} as unknown as ImageUrlBuilder

export function urlFor(source: ExtendedSanityImageSource): ImageUrlBuilder {
  if (!builder) {
    console.warn('Sanity builder not initialized, returning empty URL');
    return stubBuilder
  }

  // Check if source is a string (URL) or object with asset reference
  if (typeof source === 'string') {
    // If it's already a URL string, return it directly
    console.log('Image source is already a URL string:', source);
    return builder.image(source)
  }

  // If it's an object, check for asset reference
  // The structure depends on how Sanity returns the data
  if (!source) {
    console.warn('Invalid image source provided to urlFor:', source);
    return stubBuilder
  }

  // Check if it's an image object with asset reference
  const hasAssetRef = 'asset' in source && source.asset && source.asset._ref;
  if (!hasAssetRef) {
    console.warn('Image source does not have a valid asset reference:', source);
    return stubBuilder
  }

  console.log('Processing image source in urlFor:', source);
  return builder.image(source)
}
